import _ from 'underscore';
import React, { useState, useMemo, useEffect, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { Modal, mergeStyles } from '@bingads-webui-react/fabric-control';
import { useBoolean } from '@fluentui/react-hooks';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { withSiteMap, getRoutePathById, processorSet } from '@bingads-webui-react/with-site-map';
import URI from 'urijs';
import { Constants as CCUIConstants } from '@bingads-webui-clientcenter/entity-utils';
import { withLock } from '@bingads-webui-react/popup-manager';
import { Frontdoor } from './frontdoor';
import { NotInterested } from './not-interested';
import { RemindLater } from './remind-later';
import { Claimed } from './claimed';
import { useHideCalloutPreference } from './use-hide-callout-preference';
import { getDaysToExpiration } from './utils';
import RecommendationIcon from '../media/RecommendationIcon.svg';
import CouponIcon from '../media/CouponIcon.svg';

const FAC_STATUS_ACTIVE = CCUIConstants.RecommendationAdoptionStatus.Active;

const CALLOUT_STATES = Object.freeze({
  FRONTDOOR: 0,
  NOT_INTERESTED: 1,
  REMIND_LATER: 2,
  CLAIMED: 3,
});

const modalStyles = mergeStyles({
  width: 600,
  padding: 24,
});

const overlayStyles = {
  root: {
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
  },
};

export const FacCalloutInternal = ({
  coupon, onActivate, userPreferenceService, i18n, siteMap, steps,
  legacyScope, legacyInstrumentation, scenarioName, refreshUserPreferencesCache,
  shouldHideForDaysAfterDismiss = false,
}) => {
  const [isModalOpen, { setTrue: showModal, setFalse: hideModal }] = useBoolean(false);
  const [currentStep, setCurrentStep] = useState(CALLOUT_STATES.FRONTDOOR);

  const logWithPrefix = useCallback(log => (scenarioName ? `[${scenarioName}]: ${log}` : log), [scenarioName]);
  const couponSteps = useMemo(() => {
    if (_.isEmpty(steps)) {
      return [{
        stepIndex: 1,
        icon: RecommendationIcon,
        description: i18n.getString('Coupon_LightboxStep1Description', coupon),
      }, {
        stepIndex: 2,
        icon: CouponIcon,
        description: i18n.getString('Coupon_LightboxStep3Description', coupon),
      }];
    }
    return steps;
  }, [steps, coupon, i18n]);

  const { scope } = useContext(ScopeContext) || {};
  const finalScope = useMemo(() => scope || legacyScope, [scope, legacyScope]);

  const { uid, cid, aid } = finalScope.values;

  const couponDetailsUrl = useMemo(() => {
    try {
      return new URI(getRoutePathById(siteMap, 'coupon')).search({ uid, cid, aid }).href();
    } catch (e) {
      return null;
    }
  }, [aid, cid, siteMap, uid]);

  useEffect(() => {
    if (coupon.rawData.Status === FAC_STATUS_ACTIVE) {
      setCurrentStep(CALLOUT_STATES.CLAIMED);
    }
  }, [coupon.rawData.Status]);

  const {
    hideCalloutForEver,
    hideCalloutFor7Days,
    hideCalloutFor14Days,
    isCalloutHidden,
    activity,
  } = useHideCalloutPreference({
    coupon: coupon.rawData,
    userPreferenceService,
    scope: finalScope,
    legacyInstrumentation,
    scenarioName,
    refreshUserPreferencesCache,
  });

  const daysToExpiration = useMemo(() => getDaysToExpiration(coupon.expirationDate), [coupon.expirationDate]);

  const hideForever = useCallback(() => {
    activity.trace(logWithPrefix('hide callout forever'));
    hideModal();
    hideCalloutForEver();
  }, [hideCalloutForEver, hideModal, activity, logWithPrefix]);

  const onNotInterestedFrontdoorClick = useCallback(() => {
    activity.trace(logWithPrefix('click not interested and hide forever'));
    hideCalloutForEver();
    setCurrentStep(CALLOUT_STATES.NOT_INTERESTED);
  }, [hideCalloutForEver, activity, logWithPrefix]);

  const onRemindMeLaterFrontdoorClick = useCallback(() => {
    if (daysToExpiration < 14) {
      activity.trace(logWithPrefix('onRemindMeLaterFrontdoorClick and expired in 14 days'));
      setCurrentStep(CALLOUT_STATES.REMIND_LATER);
    } else {
      activity.trace(logWithPrefix('onRemindMeLaterFrontdoorClick and hide for 7 days and expired > 14 days'));
      hideModal();
      hideCalloutFor7Days();
    }
  }, [daysToExpiration, hideCalloutFor7Days, hideModal, activity, logWithPrefix]);

  const onRemindMeLaterClick = useCallback(() => {
    activity.trace(logWithPrefix('onRemindMeLaterClick and hide for 7 days'));
    hideModal();
    hideCalloutFor7Days();
  }, [hideCalloutFor7Days, hideModal, activity, logWithPrefix]);

  const onActivateClick = useCallback(() => {
    activity.trace(logWithPrefix('onActivateClick and hide for 14 days'));
    hideModal();
    hideCalloutFor14Days().then(onActivate);
  }, [hideCalloutFor14Days, hideModal, onActivate, activity, logWithPrefix]);

  const onClaimedConfirmClick = useCallback(() => {
    activity.trace(logWithPrefix('onClaimedConfirmClick and hide for 14 days'));
    hideModal();
    hideCalloutFor14Days();
  }, [hideCalloutFor14Days, hideModal, activity, logWithPrefix]);

  const onDismissClick = useCallback(() => {
    hideModal();
    if (shouldHideForDaysAfterDismiss) {
      activity.trace(logWithPrefix('onDismissClick and hide for 7 days'));
      hideCalloutFor7Days();
    } else {
      activity.trace(logWithPrefix('onDismissClick'));
    }
  }, [hideModal, activity, logWithPrefix, hideCalloutFor7Days, shouldHideForDaysAfterDismiss]);

  useEffect(() => {
    if (!isCalloutHidden) {
      showModal();
      activity.trace(logWithPrefix('fac callout show'));
    } else {
      hideModal();
    }
  }, [showModal, hideModal, isCalloutHidden, activity, logWithPrefix]);

  const modalContent = useMemo(() => {
    switch (currentStep) {
      case CALLOUT_STATES.FRONTDOOR:
        return (<Frontdoor
          coupon={coupon}
          i18n={i18n}
          onNotInterested={onNotInterestedFrontdoorClick}
          onDismiss={onDismissClick}
          onActivate={onActivateClick}
          onRemindLater={onRemindMeLaterFrontdoorClick}
          steps={couponSteps}
        />);
      case CALLOUT_STATES.NOT_INTERESTED:
        return (<NotInterested
          i18n={i18n}
          onDismiss={hideModal}
          couponDetailsUrl={couponDetailsUrl}
        />);
      case CALLOUT_STATES.REMIND_LATER:
        return (<RemindLater
          coupon={coupon}
          i18n={i18n}
          onNotInterested={hideForever}
          onActivate={onActivateClick}
          onConfirm={onRemindMeLaterClick}
          onDismiss={onDismissClick}
        />);
      case CALLOUT_STATES.CLAIMED:
        return (<Claimed
          coupon={coupon}
          i18n={i18n}
          onConfirm={onClaimedConfirmClick}
          onDismiss={hideForever}
          steps={couponSteps}
        />);
      default:
        return null;
    }
  }, [currentStep, coupon, i18n, onNotInterestedFrontdoorClick, onActivateClick,
    onRemindMeLaterFrontdoorClick, couponDetailsUrl, hideForever, onRemindMeLaterClick,
    onClaimedConfirmClick, couponSteps, onDismissClick]);

  const Callout = useCallback(() => (
    <Modal
      className="fac-callout"
      isOpen={isModalOpen}
      overlay={{ styles: overlayStyles }}
      isBlocking
    >
      <div className={modalStyles}>
        {modalContent}
      </div>
    </Modal>
  ), [isModalOpen, modalContent]);

  const CalloutWithLock = withLock(Callout, { force: true });

  return (
    <>
      {isModalOpen &&
        <CalloutWithLock />
      }
    </>
  );
};

FacCalloutInternal.propTypes = {
  coupon: PropTypes.shape({
    expirationDate: PropTypes.string,
    rawData: PropTypes.shape({
      Status: PropTypes.number.isRequired,
    }),
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  onActivate: PropTypes.func.isRequired,
  siteMap: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  userPreferenceService: PropTypes.shape({}).isRequired,
  steps: PropTypes.arrayOf(PropTypes.shape({
    stepIndex: PropTypes.number,
    description: PropTypes.string,
    icon: PropTypes.string,
  })).isRequired,
  legacyScope: PropTypes.shape({
    values: PropTypes.shape({
      uid: PropTypes.string,
      cid: PropTypes.string,
      aid: PropTypes.string,
    }),
  }),
  legacyInstrumentation: PropTypes.shape({}),
  scenarioName: PropTypes.string,
  refreshUserPreferencesCache: PropTypes.func,
  shouldHideForDaysAfterDismiss: PropTypes.bool,
};

FacCalloutInternal.defaultProps = {
  legacyScope: null,
  legacyInstrumentation: null,
  scenarioName: null,
  refreshUserPreferencesCache: null,
  shouldHideForDaysAfterDismiss: false,
};

export const FacCallout = withSiteMap(FacCalloutInternal, processorSet.basic);
